import React, { useEffect, useMemo, useRef, useState } from 'react';
import TopNav from '../components/TopNav';
import { darkPurple, accent } from '../utils/colors';
import { fetchUserSkills, updateLearningStatus, fetchSkillToUser, updateSkillToUser, fetchAllSkills } from '../utils/api';
import SkillBoardModal from '../components/SkillBoardModal';

// Единый источник истины для колонок-статусов (соответствует Display(Name) из LearningStatus)
const STATUS_LABELS = [
  'Запланировано изучение',
  'В процессе изучения',
  'Готово к сдаче',
  'Подтверждён',
];

export default function StatusBoardPage({ apiBase }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [parentId, setParentId] = useState('all');
  const [statusLabels, setStatusLabels] = useState(STATUS_LABELS);
  const [columns, setColumns] = useState(() => ({
    [STATUS_LABELS[0]]: [],
    [STATUS_LABELS[1]]: [],
    [STATUS_LABELS[2]]: [],
    [STATUS_LABELS[3]]: [],
  }));
  const dragGhostRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedSkillToUser, setSelectedSkillToUser] = useState(null);
  const [externalTaskId, setExternalTaskId] = useState('');
  const [note, setNote] = useState('');
  const [rootSkills, setRootSkills] = useState([]);

  // Load user's skills via new my-skills contract (flat list of SkillToUserModel)
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!apiBase) return;
      setLoading(true);
      setError('');
      try {
        const parentSkillIdParam = parentId === 'all' ? null : Number(parentId);
        const payload = await fetchUserSkills(apiBase, parentSkillIdParam);
        if (!cancelled) setData(Array.isArray(payload) ? payload : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Ошибка загрузки данных');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [apiBase, parentId]);

  // Load all skills for dropdown (no filters)
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!apiBase) return;
      try {
        const skills = await fetchAllSkills(apiBase);
        if (!cancelled) setRootSkills(Array.isArray(skills) ? skills : []);
      } catch {}
    }
    run();
    return () => { cancelled = true; };
  }, [apiBase]);

  const parentOptions = useMemo(() => {
    const arr = Array.isArray(rootSkills) ? rootSkills : [];
    return arr
      .map(s => ({ id: s.id, title: s.title }))
      .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'ru'));
  }, [rootSkills]);

  // Жёстко фиксируем порядок колонок согласно enum LearningStatus (Display(Name))
  useEffect(() => { setStatusLabels(STATUS_LABELS); }, [data]);

  // Build columns from flat list according to LearningStatus enum index
  useEffect(() => {
    const next = {};
    for (const lbl of statusLabels) next[lbl] = [];
    const items = Array.isArray(data) ? data : [];
    for (const it of items) {
      const sk = it?.skill || {};
      const pp = Array.isArray(sk.parentPath) && sk.parentPath.length > 0 ? sk.parentPath[0] : null;
      const immediateParentId = pp ? pp.id : (sk.parent ? sk.parent.id : sk.parentId);
      if (parentId !== 'all' && String(immediateParentId) !== String(parentId)) continue;
      const card = {
        id: sk.id,
        name: sk.title,
        parentId: immediateParentId,
        skillToUserId: it?.id ?? null,
        externalTaskId: it?.externalTaskId ?? '',
        comments: it?.comments ?? ''
      };
      const statusIndex = typeof it?.learningStatus === 'number' ? it.learningStatus : -1;
      const status = statusIndex >= 0 && statusIndex < statusLabels.length ? statusLabels[statusIndex] : null;
      if (status && next[status]) next[status].push(card);
    }
    setColumns(next);
  }, [data, parentId, statusLabels]);

  function onDragStart(e, card, node) {
    e.dataTransfer.setData('text/plain', JSON.stringify(card));
    try {
      if (node) {
        // Force full visibility of the original card while dragging
        node.style.opacity = '1';
        node.style.filter = 'none';
        node.style.transition = 'none';

        const clone = node.cloneNode(true);
        clone.style.position = 'fixed';
        clone.style.top = '-1000px';
        clone.style.left = '-1000px';
        clone.style.opacity = '1';
        clone.style.pointerEvents = 'none';
        document.body.appendChild(clone);
        dragGhostRef.current = clone;
        e.dataTransfer.setDragImage(clone, 0, 0);
      }
    } catch {}
  }
  function onDragEnd() {
    const ghost = dragGhostRef.current;
    if (ghost && ghost.parentNode) {
      ghost.parentNode.removeChild(ghost);
    }
    dragGhostRef.current = null;
  }

  function onCardClick(card) {
    setSelectedCard(card);
    setModalOpen(true);
    // Ленивая загрузка деталей SkillToUser при открытии модалки
    if (card.skillToUserId) {
      fetchSkillToUser(apiBase, card.skillToUserId)
        .then(su => {
          setSelectedSkillToUser(su);
          setExternalTaskId(su.externalTaskId ?? '');
          setNote(su.comments ?? '');
        })
        .catch(() => {
          setSelectedSkillToUser(null);
          setExternalTaskId('');
          setNote('');
        });
    } else {
      setSelectedSkillToUser(null);
      setExternalTaskId('');
      setNote('');
    }
  }

  function onDrop(e, targetStatus) {
    e.preventDefault();
    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    const card = JSON.parse(raw);

    const sourceStatus = Object.keys(columns).find(status => 
      columns[status].some(c => c.id === card.id)
    );
    
    // Если целевой статус такой же как исходный - ничего не делаем
    if (sourceStatus === targetStatus) {
      return;
    }

    setColumns(prev => {
      const copy = {};
      for (const lbl of statusLabels) {
        copy[lbl] = (prev[lbl] || []).filter(c => c.id !== card.id);
      }
      const fallback = statusLabels[0];
      const key = (targetStatus && copy[targetStatus]) ? targetStatus : fallback;
      copy[key] = copy[key] || [];
      copy[key].push(card);
      return copy;
    });
    const skillToUserId = card.skillToUserId;
    if (skillToUserId && targetStatus) {
      const statusIndex = statusLabels.indexOf(targetStatus);
      if (statusIndex >= 0) {
        updateLearningStatus(apiBase, skillToUserId, statusIndex).catch(() => {});
      }
    }
  }

  function allowDrop(e) {
    e.preventDefault();
  }

  return (
    <div className="App" style={{ background: darkPurple, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <TopNav current="board" />
      <div style={{ padding: 12 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 4px 24px #a259f733' }}>
          <div style={{ color: accent, fontWeight: 700, fontSize: 20, marginBottom: 12 }}>Навыки сотрудников</div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ color: '#666', marginBottom: 6 }}>Родительский навык</div>
              <select value={parentId} onChange={e => setParentId(e.target.value)} style={{ padding: '8px 10px', borderRadius: 8 }}>
                <option value="all">Все</option>
                {parentOptions.map(p => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={{ color: '#666', marginBottom: 6 }}>Пользователь</div>
              <select disabled style={{ padding: '8px 10px', borderRadius: 8, color: '#999' }}>
                <option>Скоро будет</option>
              </select>
            </div>
          </div>

          {loading && <div style={{ color: '#888' }}>Загрузка...</div>}
          {error && <div style={{ color: 'tomato' }}>{error}</div>}

          {!loading && !error && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${statusLabels.length}, 1fr)`, gap: 12 }}>
              {statusLabels.map(label => (
                <BoardColumn key={label} title={label} items={columns[label] || []} onDragStart={onDragStart} onDragEnd={onDragEnd} onDrop={onDrop} allowDrop={allowDrop} dropKey={label} onCardClick={onCardClick} />
              ))}
            </div>
          )}

          <SkillBoardModal 
            isOpen={modalOpen} 
            onClose={() => setModalOpen(false)}
            onSave={() => {
              if (!selectedSkillToUser) { setModalOpen(false); return; }
              updateSkillToUser(apiBase, selectedSkillToUser)
                .catch(() => {})
                .finally(() => setModalOpen(false));
            }}
          >
            {selectedCard ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{selectedCard.name}</div>
                <div>
                  <div style={{ marginBottom: 6, color: '#555' }}>ID задачи</div>
                  <input
                    type="number"
                    placeholder="Введите ID задачи"
                    value={externalTaskId}
                    onChange={e => {
                      const v = e.target.value;
                      setExternalTaskId(v);
                      setSelectedSkillToUser(prev => prev ? { ...prev, externalTaskId: v === '' ? null : Number(v) } : prev);
                    }}
                    style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, display: 'block' }}
                  />
                </div>
                <div>
                  <div style={{ marginBottom: 6, color: '#555' }}>Заметка</div>
                  <textarea
                    rows={12}
                    placeholder="Добавьте заметку по навыку"
                    value={note}
                    onChange={e => {
                      const v = e.target.value;
                      setNote(v);
                      setSelectedSkillToUser(prev => prev ? { ...prev, comments: v } : prev);
                    }}
                    style={{ width: '100%', maxWidth: '100%', minHeight: 360, boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, resize: 'vertical', display: 'block', wordBreak: 'break-word', overflowWrap: 'break-word' }}
                  />
                </div>
              </div>
            ) : null}
          </SkillBoardModal>
        </div>
      </div>
    </div>
  );
}

function BoardColumn({ title, items, onDragStart, onDragEnd, onDrop, allowDrop, dropKey, onCardClick }) {
  return (
    <div onDragOver={allowDrop} onDrop={e => onDrop(e, dropKey)} style={{ background: '#fff', borderRadius: 12, padding: 12, minHeight: 320, boxShadow: '0 4px 24px #a259f733' }}>
      <div style={{ fontWeight: 700, color: accent, marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(card => (
          <div key={card.id} draggable onDragStart={e => onDragStart(e, card, e.currentTarget)} onDragEnd={onDragEnd} onClick={() => onCardClick && onCardClick(card)} style={{ border: `1.5px solid ${accent}`, borderRadius: 10, padding: '10px 12px', background: '#faf7ff', color: '#333', cursor: 'pointer' }}>
            {card.name}
          </div>
        ))}
      </div>
    </div>
  );
}


